import { rcControllerSvg } from '@/lib/map/markers/rfIcons';
import { rfTargetsToShow, rfVisibilityKey } from '@/lib/map/rfVisibility';
import { useSurvStore } from '@/lib/store/surv';
import { useTargetsStore } from '@/lib/store/targets';
import { useUiStore } from '@/lib/store/ui';
/**
 * Operator (remote controller) markers for RF-detected drones (ENG-253).
 * One DOM marker per drone with a decoded operator position; the dotted link
 * to the drone is drawn by `layers/rfLinks.ts`. The glyph is a placeholder
 * RC-controller silhouette until the design icon lands.
 */
import mapboxgl from 'mapbox-gl';
import type { Map as MapboxMap } from 'mapbox-gl';

const SIZE = 30;

function buildEl(serial: string): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = `width:${SIZE}px;height:${SIZE}px;pointer-events:none`;
  el.title = `Operator · ${serial}`;
  el.innerHTML = rcControllerSvg(SIZE);
  return el;
}

export function subscribeRfOperatorMarkers(map: MapboxMap): () => void {
  const active = new Map<string, { marker: mapboxgl.Marker; lng: number; lat: number }>();

  const sync = () => {
    const seen = new Set<string>();
    for (const t of rfTargetsToShow()) {
      const op = t.rf?.operator;
      if (!op) continue;
      seen.add(t.target_id);
      const cur = active.get(t.target_id);
      if (cur) {
        // The operator rarely moves; skip the marker update when it hasn't.
        if (cur.lng !== op.lng || cur.lat !== op.lat) {
          cur.marker.setLngLat([op.lng, op.lat]);
          cur.lng = op.lng;
          cur.lat = op.lat;
        }
      } else {
        const marker = new mapboxgl.Marker({
          element: buildEl(t.rf?.serial ?? t.target_id),
          anchor: 'center',
        })
          .setLngLat([op.lng, op.lat])
          .addTo(map);
        active.set(t.target_id, { marker, lng: op.lng, lat: op.lat });
      }
    }
    for (const [id, { marker }] of active) {
      if (seen.has(id)) continue;
      marker.remove();
      active.delete(id);
    }
  };

  // Coalesce store ticks into one pass per animation frame, like the link layer.
  let rafId: number | null = null;
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      sync();
    });
  };

  sync();
  const unsubT = useTargetsStore.subscribe((s) => s.byId, schedule);
  const unsubUi = useUiStore.subscribe((s) => s.activeContext, schedule);
  const unsubSurv = useSurvStore.subscribe(rfVisibilityKey, schedule);
  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    unsubT();
    unsubUi();
    unsubSurv();
    for (const { marker } of active.values()) marker.remove();
    active.clear();
  };
}
